/**
 * Bulk Order Hero — Services section
 * Single jersey video per page (one sport per instance), composited via
 * canvas the same way as the homepage rotator: stacked color(top)+alpha-
 * mask(bottom) video, painted live so it renders with genuine per-pixel
 * transparency. No rotation, no dots, no swipe — just one visual.
 *
 * The poster image stays visible until the canvas draws its first real
 * frame, then fades out. Without this, the poster (a static pose) sits
 * permanently under the canvas and shows through wherever the mask video's
 * edges are semi-transparent, producing a doubled/ghosted jersey.
 */
(function () {
  'use strict';

  function initBulkHero(root) {
    var video = root.querySelector('[data-bulk-hero-source]');
    var canvas = root.querySelector('[data-bulk-hero-canvas]');
    var poster = root.querySelector('.bulk-hero-poster');
    if (!video || !canvas) return;

    var bufferCanvas, bufferCtx, halfW, halfH, ctx;
    var ready = false;
    var rafId = null;
    var posterHidden = false;

    function hidePoster() {
      if (poster && !posterHidden) {
        poster.classList.add('is-hidden');
        posterHidden = true;
      }
    }

    function showPoster() {
      if (poster && posterHidden) {
        poster.classList.remove('is-hidden');
        posterHidden = false;
      }
    }

    function setup() {
      halfW = video.videoWidth;
      halfH = video.videoHeight / 2;
      if (!halfW || !halfH) return;

      bufferCanvas = document.createElement('canvas');
      bufferCanvas.width = halfW;
      bufferCanvas.height = video.videoHeight;
      bufferCtx = bufferCanvas.getContext('2d', { willReadFrequently: true });

      canvas.width = halfW;
      canvas.height = halfH;
      ctx = canvas.getContext('2d');

      ready = true;
    }

    function draw() {
      if (!ready) {
        rafId = requestAnimationFrame(draw);
        return;
      }

      bufferCtx.drawImage(video, 0, 0, halfW, video.videoHeight);
      var full = bufferCtx.getImageData(0, 0, halfW, video.videoHeight).data;

      var out = ctx.createImageData(halfW, halfH);
      var outData = out.data;
      var maskOffset = halfW * halfH * 4;
      var count = halfW * halfH;

      for (var i = 0; i < count; i++) {
        var ci = i * 4;
        var mi = maskOffset + ci;
        outData[ci] = full[ci];
        outData[ci + 1] = full[ci + 1];
        outData[ci + 2] = full[ci + 2];
        outData[ci + 3] = full[mi];
      }

      ctx.putImageData(out, 0, 0);

      /* First real frame is on-canvas now — safe to fade the poster out. */
      hidePoster();

      rafId = requestAnimationFrame(draw);
    }

    function stopDrawing() {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function play() {
      // Skip video entirely on mobile: no download, no per-frame canvas
      // compositing (the real CPU cost of this technique). The poster is
      // already a genuine transparent image, so nothing is lost visually
      // besides the motion.
      var isMobile = window.matchMedia('(max-width: 989px)').matches;
      if (isMobile) return;

      video.muted = true;
      video.preload = 'auto';
      video.addEventListener('loadedmetadata', setup, { once: true });
      if (video.readyState >= 1 && !ready) setup();

      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
      stopDrawing();
      rafId = requestAnimationFrame(draw);
    }

    function pause() {
      video.pause();
      stopDrawing();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        play();
      } else {
        pause();
      }
    });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              play();
            } else {
              pause();
            }
          });
        },
        { threshold: 0.15 }
      );
      observer.observe(root);
    } else {
      play();
    }

    video.setAttribute('playsinline', '');
    video.playsInline = true;
    video.loop = true;

    // If the video fails outright (bad source, decode error), keep the
    // poster visible instead of leaving a blank canvas on screen.
    video.addEventListener('error', showPoster);
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-bulk-hero]');
    roots.forEach(initBulkHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    var root = evt.target.querySelector('[data-bulk-hero]');
    if (root) initBulkHero(root);
  });
})();